import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, TextField, MenuItem, CircularProgress, Alert, Typography,
  Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import { Search, Visibility } from '@mui/icons-material';
import { issueService, type Issue } from '../../services/issue.service';
import { colors } from '../../theme/theme';

const STATUS_META: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'default' }> = {
  open: { label: 'Open', color: 'warning' },
  in_progress: { label: 'In progress', color: 'info' },
  resolved: { label: 'Resolved', color: 'success' },
  closed: { label: 'Closed', color: 'default' },
};
const CATEGORY_LABEL: Record<string, string> = {
  bribe: 'Bribery', 'wrong-charge': 'Wrong charge', technical: 'Technical', other: 'Other',
};

export const SuperAdminIssuesPage = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [view, setView] = useState<Issue | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['sa-issues', { search, status }],
    queryFn: () => issueService.listAll({ search: search || undefined, status: status || undefined, per_page: 50 }),
  });
  const issues = data?.data ?? [];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Reported Issues"
          subtitle="Citizen and staff issue reports across all counties."
          breadcrumbs={[{ label: 'Dashboard', path: '/superadmin/dashboard' }, { label: 'Issues' }]}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search reference or text" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: colors.grey[500] }} fontSize="small" /> }} sx={{ minWidth: 280 }} />
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>Failed to load issues.</Alert>}

        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>County</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reporter</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">View</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issues.map((i) => {
                    const meta = STATUS_META[i.status] ?? { label: i.status, color: 'default' as const };
                    return (
                      <TableRow key={i.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{i.reference}</TableCell>
                        <TableCell>{i.county_name ?? '—'}</TableCell>
                        <TableCell>
                          <Chip label={CATEGORY_LABEL[i.category] ?? i.category} size="small"
                            color={i.category === 'bribe' ? 'error' : 'default'} variant="outlined" />
                        </TableCell>
                        <TableCell>{i.reporter_name}</TableCell>
                        <TableCell><Chip label={meta.label} color={meta.color} size="small" /></TableCell>
                        <TableCell align="right">
                          <Tooltip title="View"><IconButton size="small" onClick={() => setView(i)} sx={{ color: colors.primary.main }}><Visibility fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {issues.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No issues found</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Dialog open={!!view} onClose={() => setView(null)} maxWidth="sm" fullWidth>
          {view && (
            <>
              <DialogTitle>
                {view.reference}
                <Chip label={STATUS_META[view.status]?.label ?? view.status} color={STATUS_META[view.status]?.color ?? 'default'} size="small" sx={{ ml: 1 }} />
              </DialogTitle>
              <DialogContent>
                <Typography variant="caption" color="text.secondary">County</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{view.county_name ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">Category</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{CATEGORY_LABEL[view.category] ?? view.category}</Typography>
                <Typography variant="caption" color="text.secondary">Reporter</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{view.reporter_name}</Typography>
                <Typography variant="caption" color="text.secondary">Details</Typography>
                <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>{view.message}</Typography>
                {view.resolution_note && (<><Typography variant="caption" color="text.secondary">Resolution note</Typography><Typography variant="body2">{view.resolution_note}</Typography></>)}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
